import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { userService, type User } from '@/services/userService'

interface ResetPasswordModalProps {
  onSuccess?: () => void
}

export interface ResetPasswordModalRef {
  open: (user: User) => void
  close: () => void
}

const ResetPasswordModal = forwardRef<ResetPasswordModalRef, ResetPasswordModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('users')
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  useImperativeHandle(ref, () => ({
    open: (userData: User) => {
      setUser(userData)
      setIsOpen(true)
      setError('')
      setSuccess(false)
      setNewPassword('')
      setConfirmPassword('')
    },
    close: () => {
      setIsOpen(false)
      setUser(null)
      setNewPassword('')
      setConfirmPassword('')
      setSuccess(false)
    },
  }))

  const isPasswordValid = newPassword.length >= 6
  const isPasswordMatch = newPassword === confirmPassword
  const canSubmit = isPasswordValid && isPasswordMatch && !loading

  const handleResetPassword = async () => {
    if (!user || !canSubmit) return

    try {
      setLoading(true)
      setError('')

      await userService.resetPassword(user.id, newPassword)

      // Show success message instead of closing immediately
      setSuccess(true)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, errorMessage))
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    const wasSuccess = success
    setIsOpen(false)
    setUser(null)
    setNewPassword('')
    setConfirmPassword('')
    setError('')
    setSuccess(false)

    // Only call onSuccess if the operation was successful
    if (wasSuccess) {
      onSuccess?.()
    }
  }

  if (!isOpen || !user) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/30 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-orange-600">{t('resetPasswordModal.title', 'Reset Password')}</h2>
              <p className="text-sm text-gray-600 mt-1">{t('resetPasswordModal.subtitle', 'Set a new password for this user')}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Success Message */}
          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-900 mb-1">
                    {t('resetPasswordModal.successTitle', 'Password reset successfully!')}
                  </p>
                  <p className="text-xs text-green-700">
                    {t('resetPasswordModal.successDesc', 'The user can now log in with the new password.')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && !success && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* User Info */}
          {!success && (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-3">{t('resetPasswordModal.message', 'Enter a new password for the following user:')}</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('table.account', 'Account')}:</span>
                <span className="text-sm text-gray-900">{user.account}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('table.name', 'Name')}:</span>
                <span className="text-sm text-gray-900">{user.name}</span>
              </div>
            </div>
          </div>
          )}

          {/* Password Input */}
          {!success && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('resetPasswordModal.newPassword', 'New Password')}
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('resetPasswordModal.enterNewPassword', 'Enter new password')}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                newPassword && !isPasswordValid
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-primary'
              }`}
              disabled={loading}
            />
            {newPassword && !isPasswordValid && (
              <p className="text-sm text-red-600 mt-1">
                {t('resetPasswordModal.passwordMinLength', 'Password must be at least 6 characters')}
              </p>
            )}
          </div>
          )}

          {/* Confirm Password Input */}
          {!success && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('resetPasswordModal.confirmPassword', 'Confirm Password')}
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('resetPasswordModal.enterConfirmPassword', 'Confirm new password')}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                confirmPassword && !isPasswordMatch
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-primary'
              }`}
              disabled={loading}
            />
            {confirmPassword && !isPasswordMatch && (
              <p className="text-sm text-red-600 mt-1">
                {t('resetPasswordModal.passwordMismatch', 'Passwords do not match')}
              </p>
            )}
          </div>
          )}

          {/* Info Box */}
          {!success && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-blue-700">
                  {t('resetPasswordModal.info', 'The user will need to use this new password to log in. Make sure to communicate the new password securely.')}
                </p>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          {success ? (
            <button
              type="button"
              onClick={handleClose}
              className="px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              {t('resetPasswordModal.close', 'Close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                {t('resetPasswordModal.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleResetPassword}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={!canSubmit}
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{loading ? t('resetPasswordModal.resetting', 'Resetting') : t('resetPasswordModal.reset', 'Reset Password')}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
})

ResetPasswordModal.displayName = 'ResetPasswordModal'

export default ResetPasswordModal

